/**
 * Smart Shikka AI Core — Aiko Icon Registry/UI Preview Registry
 * PR83: No runtime injection. Deep-frozen preview metadata.
 * Adapted from Aiko (https://github.com/tw93/aiko), MIT license (in README).
 *
 * Contains:
 * - 1 asset-level preview (aiko_icons_svg) as sourceIconSet metadata
 * - 10 Smart Shikka UI previews for specific UI use cases
 */
const BLOCKED_ACTIONS = [
    { action: "inject_aiko_icon_runtime", reason: "No Aiko runtime injection in PR83" },
    { action: "dom_insert_svg", reason: "No DOM SVG insertion in PR83" },
    { action: "dom_mutate", reason: "No DOM mutation in PR83" },
    { action: "svg_mutate", reason: "No SVG mutation in PR83" },
    { action: "css_inject_icon", reason: "No CSS icon injection in PR83" },
    { action: "render_svg_live", reason: "No live SVG rendering in PR83" },
    { action: "import_svg_component", reason: "No SVG component import in PR83" },
    { action: "browser_render", reason: "No browser rendering in PR83" },
    { action: "file_mutate", reason: "No file mutation in PR83" },
    { action: "network_call", reason: "No network calls in PR83" },
    { action: "provider_call", reason: "No provider calls in PR83" },
    { action: "db_write", reason: "No DB writes in PR83" },
    { action: "supabase_call", reason: "No Supabase calls in PR83" },
    { action: "secret_read", reason: "No secret access in PR83" },
    { action: "file_read", reason: "No file reads in PR83" },
    { action: "file_write", reason: "No file writes in PR83" },
    { action: "child_process", reason: "No child_process in PR83" },
    { action: "shell_command", reason: "No shell commands in PR83" },
    { action: "browser_action", reason: "No browser automation in PR83" },
    { action: "desktop_action", reason: "No desktop automation in PR83" },
];
function deepFreeze(obj) {
    Object.freeze(obj);
    for (const value of Object.values(obj)) {
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return obj;
}
function makePreview(id, displayName, description, uiUseCase) {
    return {
        id,
        displayName,
        description,
        sourceIconSet: "packages/core/src/aiko-icon-system/icons.svg",
        uiUseCase,
        previewOnly: true,
        runtimeInjectionAllowedInPr83: false,
        domMutationAllowedInPr83: false,
        svgMutationAllowedInPr83: false,
        browserRenderAllowedInPr83: false,
        fileMutationAllowedInPr83: false,
        networkAllowedInPr83: false,
        licenseVerificationRequired: true,
        attributionRequired: true,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: UI icon integration with owner approval"],
        notes: [
            "Source: Aiko icons.svg, https://github.com/tw93/aiko, commit b7ba4c7ddff6416760f0cdf5b53d6cdd65fd1a12",
            "MIT license (in README)",
        ],
    };
}
const previews = [
    // Asset-level preview (existing)
    makePreview("aiko_icons_svg", "Smart Shikka AI Icon Set Preview, adapted from Aiko", "Aiko icon set (icons.svg) asset-level preview — metadata only, no runtime injection", "icon_asset"),
    // 10 Smart Shikka UI previews
    makePreview("assistant_control_center", "Smart Shikka AI Assistant Control Center Icon Preview, adapted from Aiko", "Icon preview for the assistant control center UI — metadata only, no runtime injection", "assistant_control_center"),
    makePreview("document_rendering", "Smart Shikka AI Document Rendering Icon Preview, adapted from Aiko", "Icon preview for document rendering UI — metadata only, no runtime injection", "document_rendering"),
    makePreview("workflow_planning", "Smart Shikka AI Workflow Planning Icon Preview, adapted from Aiko", "Icon preview for workflow planning UI — metadata only, no runtime injection", "workflow_planning"),
    makePreview("safety_guardrail", "Smart Shikka AI Safety Guardrail Icon Preview, adapted from Aiko", "Icon preview for safety guardrail UI — metadata only, no runtime injection", "safety_guardrail"),
    makePreview("desktop_shell", "Smart Shikka AI Desktop Shell Icon Preview, adapted from Aiko", "Icon preview for desktop shell UI — metadata only, no runtime injection", "desktop_shell"),
    makePreview("local_data", "Smart Shikka AI Local Data Icon Preview, adapted from Aiko", "Icon preview for local data UI — metadata only, no runtime injection", "local_data"),
    makePreview("approval_gate", "Smart Shikka AI Approval Gate Icon Preview, adapted from Aiko", "Icon preview for approval gate UI — metadata only, no runtime injection", "approval_gate"),
    makePreview("audit_report", "Smart Shikka AI Audit Report Icon Preview, adapted from Aiko", "Icon preview for audit report UI — metadata only, no runtime injection", "audit_report"),
    makePreview("settings", "Smart Shikka AI Settings Icon Preview, adapted from Aiko", "Icon preview for settings UI — metadata only, no runtime injection", "settings"),
    makePreview("future_assistant", "Smart Shikka AI Future Smart Shikka Assistant Icon Preview, adapted from Aiko", "Icon preview for future Smart Shikka Assistant integration UI — metadata only, no runtime injection", "future_assistant"),
];
const registry = deepFreeze({ previews });
export function getAikoIconUiPreviews() {
    return registry.previews;
}
export function getAikoIconUiPreviewById(id) {
    return registry.previews.find((p) => p.id === id);
}
export { registry as aikoIconRegistryUiPreviewRegistry };
//# sourceMappingURL=aikoIconRegistryUiPreviewRegistry.js.map